import { useCallback, useContext, useMemo } from 'react';

import { useService } from '@core/inversify-react';
import { FormikHelpers } from 'formik';
import { message } from 'antd';
import { useBus } from '@core/event-bus';

import { useShopById } from './use-shop-by-id';
import { CreateShopDto } from '../dtos';
import { CreateShopMapper } from '../mappers';
import { CreateShopContext } from '../contexts';
import { CreateShopRepo, CreateShopRepoType } from '../repos';

export const useCreateShop = () => {
  const { id, onSucceed } = useContext(CreateShopContext);
  const shop = useShopById(id, { enabled: !!id });

  const createShopRepo = useService<CreateShopRepo>(CreateShopRepoType);
  const eventBus = useBus();

  const initialValues = useMemo<CreateShopDto | undefined>(() => {
    if (id) {
      return shop.data ? CreateShopMapper.toDomain(shop.data) : undefined;
    }

    return {
      name: '',
      id: '',
      countryId: null,
    };
  }, [shop.data, id]);

  const onSubmit = useCallback(
    async (values: CreateShopDto, helpers: FormikHelpers<CreateShopDto>) => {
      helpers.setStatus(null);
      const result = await createShopRepo.execute(values);

      if (result.status === 200) {
        if (!id) {
          eventBus.publish({ type: '@shop_names/create/succeed' });
        } else {
          eventBus.publish({ type: '@shop_names/update/succeed' });
        }

        onSucceed?.();
      } else if (result.status === 422) {
        helpers.setErrors(result.response);
      } else {
        message.error(result.response);
      }
    },
    [createShopRepo, eventBus, id, onSucceed],
  );

  return { onSubmit, initialValues };
};
